// eslint-disable-next-line import/no-cycle
import schedulerService from '@/services/schedulerService';

// State
const state = {
  jobs: {
    hourly: {
      status: 'idle', lastRun: null, nextRun: null, schemasProcessed: 0,
    },
    daily: {
      status: 'idle', lastRun: null, nextRun: null, schemasProcessed: 0,
    },
    weekly: {
      status: 'idle', lastRun: null, nextRun: null, schemasProcessed: 0,
    },
    monthly: {
      status: 'idle', lastRun: null, nextRun: null, schemasProcessed: 0,
    },
    yearly: {
      status: 'idle', lastRun: null, nextRun: null, schemasProcessed: 0,
    },
  },
  executionHistory: [],
  loading: false,
  error: null,
  statistics: {
    totalExecutions: 0,
    successfulExecutions: 0,
    failedExecutions: 0,
    totalSchemasProcessed: 0,
    totalTipsRecommended: 0,
    totalUsersProcessed: 0,
  },
  filters: {
    jobType: '',
    status: '',
    dateRange: null,
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
};

// Mutations
const mutations = {
  SET_LOADING(currentState, loading) {
    // eslint-disable-next-line no-param-reassign
    currentState.loading = loading;
  },

  SET_ERROR(currentState, error) {
    // eslint-disable-next-line no-param-reassign
    currentState.error = error;
  },

  UPDATE_JOB_STATUS(currentState, {
    jobType, status, lastRun, nextRun, schemasProcessed,
  }) {
    if (currentState.jobs[jobType]) {
      // eslint-disable-next-line no-param-reassign
      currentState.jobs[jobType] = {
        ...currentState.jobs[jobType],
        status,
        lastRun,
        nextRun,
        schemasProcessed: schemasProcessed || currentState.jobs[jobType].schemasProcessed,
      };
    }
  },

  SET_JOBS_STATUS(currentState, jobsStatus) {
    // eslint-disable-next-line no-param-reassign
    currentState.jobs = { ...currentState.jobs, ...jobsStatus };
  },

  ADD_EXECUTION_RECORD(currentState, record) {
    // eslint-disable-next-line no-param-reassign
    currentState.executionHistory.unshift(record);

    // Keep only last 100 records for performance
    if (currentState.executionHistory.length > 100) {
      // eslint-disable-next-line no-param-reassign
      currentState.executionHistory = currentState.executionHistory.slice(0, 100);
    }
  },

  SET_EXECUTION_HISTORY(currentState, history) {
    // eslint-disable-next-line no-param-reassign
    currentState.executionHistory = history;
  },

  UPDATE_STATISTICS(currentState, statistics) {
    // eslint-disable-next-line no-param-reassign
    currentState.statistics = { ...currentState.statistics, ...statistics };
  },

  SET_FILTERS(currentState, filters) {
    // eslint-disable-next-line no-param-reassign
    currentState.filters = { ...currentState.filters, ...filters };
  },

  SET_PAGINATION(currentState, pagination) {
    // eslint-disable-next-line no-param-reassign
    currentState.pagination = { ...currentState.pagination, ...pagination };
  },

  CLEAR_ERROR(currentState) {
    // eslint-disable-next-line no-param-reassign
    currentState.error = null;
  },

  RESET_STATE(currentState) {
    // eslint-disable-next-line no-param-reassign
    currentState.jobs = {
      hourly: {
        status: 'idle', lastRun: null, nextRun: null, schemasProcessed: 0,
      },
      daily: {
        status: 'idle', lastRun: null, nextRun: null, schemasProcessed: 0,
      },
      weekly: {
        status: 'idle', lastRun: null, nextRun: null, schemasProcessed: 0,
      },
      monthly: {
        status: 'idle', lastRun: null, nextRun: null, schemasProcessed: 0,
      },
      yearly: {
        status: 'idle', lastRun: null, nextRun: null, schemasProcessed: 0,
      },
    };
    // eslint-disable-next-line no-param-reassign
    currentState.executionHistory = [];
    // eslint-disable-next-line no-param-reassign
    currentState.error = null;
    // eslint-disable-next-line no-param-reassign
    currentState.statistics = {
      totalExecutions: 0,
      successfulExecutions: 0,
      failedExecutions: 0,
      totalSchemasProcessed: 0,
      totalTipsRecommended: 0,
      totalUsersProcessed: 0,
    };
  },
};

// Actions
const actions = {
  /**
   * Get current scheduler status and job information
   */
  async getSchedulerStatus({ commit }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const response = await schedulerService.getStatus();

      // Update jobs status based on response
      if (response.jobs) {
        commit('SET_JOBS_STATUS', response.jobs);
      }

      return response;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to get scheduler status');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Trigger DevTips scheduling for all periods
   */
  async triggerDevTipsScheduling({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const response = await schedulerService.triggerDevTipsScheduling();

      // Add execution record
      commit('ADD_EXECUTION_RECORD', {
        id: Date.now(),
        type: 'dev-tips-all',
        timestamp: new Date().toISOString(),
        status: 'success',
        message: response.message,
        data: response,
      });

      // Update statistics
      commit('UPDATE_STATISTICS', {
        totalExecutions: state.statistics.totalExecutions + 1,
        successfulExecutions: state.statistics.successfulExecutions + 1,
      });

      // Show success notification
      dispatch(
        'notifications/showToast',
        {
          severity: 'success',
          summary: 'Success',
          detail: response.message || 'DevTips scheduling triggered successfully',
          life: 3000,
        },
        { root: true },
      );

      return response;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to trigger DevTips scheduling');

      // Add failed execution record
      commit('ADD_EXECUTION_RECORD', {
        id: Date.now(),
        type: 'dev-tips-all',
        timestamp: new Date().toISOString(),
        status: 'error',
        message: error.message,
        error: error.message,
      });

      // Update statistics
      commit('UPDATE_STATISTICS', {
        totalExecutions: state.statistics.totalExecutions + 1,
        failedExecutions: state.statistics.failedExecutions + 1,
      });

      // Show error notification
      dispatch(
        'notifications/showToast',
        {
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to trigger DevTips scheduling',
          life: 3000,
        },
        { root: true },
      );

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Trigger level-system inactivity detection
   */
  async triggerInactivityDetection({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const response = await schedulerService.triggerInactivityDetection();
      const body = response.data || response;

      commit('ADD_EXECUTION_RECORD', {
        id: Date.now(),
        type: 'inactivity-detection',
        timestamp: new Date().toISOString(),
        status: 'success',
        message: body.message || 'Inactivity detection completed',
        data: body,
      });

      commit('UPDATE_STATISTICS', {
        totalExecutions: state.statistics.totalExecutions + 1,
        successfulExecutions: state.statistics.successfulExecutions + 1,
      });

      dispatch(
        'notifications/showToast',
        {
          severity: 'success',
          summary: 'Success',
          detail: body.message || 'Inactivity detection triggered successfully',
          life: 3000,
        },
        { root: true },
      );

      return body;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to trigger inactivity detection');

      commit('ADD_EXECUTION_RECORD', {
        id: Date.now(),
        type: 'inactivity-detection',
        timestamp: new Date().toISOString(),
        status: 'error',
        message: error.message,
        error: error.message,
      });

      commit('UPDATE_STATISTICS', {
        totalExecutions: state.statistics.totalExecutions + 1,
        failedExecutions: state.statistics.failedExecutions + 1,
      });

      dispatch(
        'notifications/showToast',
        {
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to trigger inactivity detection',
          life: 3000,
        },
        { root: true },
      );

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Trigger travel buddy matching for the current tenant
   */
  async triggerTravelBuddyMatching({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const response = await schedulerService.triggerTravelBuddyMatching();
      const body = response.data || response;

      commit('ADD_EXECUTION_RECORD', {
        id: Date.now(),
        type: 'travel-buddy',
        timestamp: new Date().toISOString(),
        status: 'success',
        message: body.message || 'Travel buddy matching completed',
        data: body,
      });

      commit('UPDATE_STATISTICS', {
        totalExecutions: state.statistics.totalExecutions + 1,
        successfulExecutions: state.statistics.successfulExecutions + 1,
      });

      dispatch(
        'notifications/showToast',
        {
          severity: 'success',
          summary: 'Success',
          detail: body.message || 'Travel buddy matching triggered successfully',
          life: 3000,
        },
        { root: true },
      );

      return body;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to trigger travel buddy matching');

      commit('ADD_EXECUTION_RECORD', {
        id: Date.now(),
        type: 'travel-buddy',
        timestamp: new Date().toISOString(),
        status: 'error',
        message: error.message,
        error: error.message,
      });

      commit('UPDATE_STATISTICS', {
        totalExecutions: state.statistics.totalExecutions + 1,
        failedExecutions: state.statistics.failedExecutions + 1,
      });

      dispatch(
        'notifications/showToast',
        {
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to trigger travel buddy matching',
          life: 3000,
        },
        { root: true },
      );

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Travel Buddy Simulator: complete up to 10 random meetings, run matching, return new meetings with names and category
   */
  async simulateTravelBuddy({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const response = await schedulerService.simulateTravelBuddy();
      const body = response.data || response;

      const parts = [
        `${body.completedCount ?? 0} completed`,
        (body.skippedCount ?? 0) > 0 ? `${body.skippedCount} skipped` : null,
        `${body.newMeetings?.length ?? 0} new`,
      ].filter(Boolean);
      const detail = body.hint
        ? parts.join(', ') + '. ' + body.hint
        : 'Simulation complete: ' + parts.join(', ');
      dispatch(
        'notifications/showToast',
        {
          severity: 'success',
          summary: 'Success',
          detail,
          life: body.hint ? 6000 : 3000,
        },
        { root: true },
      );

      // Refresh travel buddy users so Travel Buddy Manager "Users Status" shows updated future/previous counts
      await dispatch('travelBuddy/fetchUsers', null, { root: true });

      return body;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Travel buddy simulation failed');

      dispatch(
        'notifications/showToast',
        {
          severity: 'error',
          summary: 'Error',
          detail: error.response?.data?.message || error.message || 'Travel buddy simulation failed',
          life: 5000,
        },
        { root: true },
      );

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * In-memory simulation (no DB): get current tenant state for travel buddy simulator
   */
  async getTravelBuddySimulationState({ commit }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      const response = await schedulerService.getTravelBuddySimulationState();
      return response.data ?? response;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to load simulation state');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * In-memory simulation (no DB): run until every pair has at least one completed meeting
   * @param {Object} _context - Vuex context
   * @param {number} skipProbability - 0–1
   */
  async runTravelBuddySimulation({ commit, dispatch }, skipProbability = 0.25) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      const response = await schedulerService.runTravelBuddySimulation(skipProbability);
      const body = response.data ?? response;
      dispatch(
        'notifications/showToast',
        {
          severity: 'success',
          summary: 'In-memory simulation complete',
          detail: `${body.summary?.totalRounds ?? 0} rounds, ${body.summary?.totalMeetings ?? 0} meetings, ${body.summary?.uniquePairsWithCompletedMeeting ?? 0}/${body.summary?.expectedPairsForFullCoverage ?? 0} pairs with at least one completed meeting.`,
          life: 5000,
        },
        { root: true },
      );
      return body;
    } catch (error) {
      commit('SET_ERROR', error.message || 'In-memory simulation failed');
      dispatch(
        'notifications/showToast',
        {
          severity: 'error',
          summary: 'Error',
          detail: error.response?.data?.message || error.message || 'In-memory simulation failed',
          life: 5000,
        },
        { root: true },
      );
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Trigger anomaly report for all tenants
   */
  async triggerAnomalyReport({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const response = await schedulerService.triggerAnomalyReport();
      const body = response.data || response;

      commit('ADD_EXECUTION_RECORD', {
        id: Date.now(),
        type: 'anomaly-report',
        timestamp: new Date().toISOString(),
        status: 'success',
        message: body.message || 'Anomaly report sent',
        data: body,
      });

      dispatch(
        'notifications/showToast',
        {
          severity: 'success',
          summary: 'Success',
          detail: body.message || 'Anomaly report triggered successfully',
          life: 3000,
        },
        { root: true },
      );

      return body;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to trigger anomaly report');

      commit('ADD_EXECUTION_RECORD', {
        id: Date.now(),
        type: 'anomaly-report',
        timestamp: new Date().toISOString(),
        status: 'error',
        message: error.message,
        error: error.message,
      });

      dispatch(
        'notifications/showToast',
        {
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to trigger anomaly report',
          life: 3000,
        },
        { root: true },
      );

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Trigger specific job type (daily, weekly, monthly, yearly)
   */
  async triggerJob({ commit, dispatch }, jobType) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      // Update job status to running
      commit('UPDATE_JOB_STATUS', {
        jobType,
        status: 'running',
        lastRun: new Date().toISOString(),
      });

      let response;
      switch (jobType) {
        case 'daily':
          response = await schedulerService.triggerDailyScheduling();
          break;
        case 'weekly':
          response = await schedulerService.triggerWeeklyScheduling();
          break;
        case 'monthly':
          response = await schedulerService.triggerMonthlyScheduling();
          break;
        case 'yearly':
          response = await schedulerService.triggerYearlyScheduling();
          break;
        default:
          throw new Error(`Unknown job type: ${jobType}`);
      }

      // Update job status to completed
      commit('UPDATE_JOB_STATUS', {
        jobType,
        status: 'completed',
        lastRun: new Date().toISOString(),
      });

      // Add execution record
      commit('ADD_EXECUTION_RECORD', {
        id: Date.now(),
        type: jobType,
        timestamp: new Date().toISOString(),
        status: 'success',
        message: response.message,
        data: response,
      });

      // Update statistics
      commit('UPDATE_STATISTICS', {
        totalExecutions: state.statistics.totalExecutions + 1,
        successfulExecutions: state.statistics.successfulExecutions + 1,
      });

      // Show success notification
      dispatch(
        'notifications/showToast',
        {
          severity: 'success',
          summary: 'Success',
          detail: response.message || `${jobType} scheduling triggered successfully`,
          life: 3000,
        },
        { root: true },
      );

      return response;
    } catch (error) {
      // Update job status to failed
      commit('UPDATE_JOB_STATUS', {
        jobType,
        status: 'failed',
        lastRun: new Date().toISOString(),
      });

      commit('SET_ERROR', error.message || `Failed to trigger ${jobType} scheduling`);

      // Add failed execution record
      commit('ADD_EXECUTION_RECORD', {
        id: Date.now(),
        type: jobType,
        timestamp: new Date().toISOString(),
        status: 'error',
        message: error.message,
        error: error.message,
      });

      // Update statistics
      commit('UPDATE_STATISTICS', {
        totalExecutions: state.statistics.totalExecutions + 1,
        failedExecutions: state.statistics.failedExecutions + 1,
      });

      // Show error notification
      dispatch(
        'notifications/showToast',
        {
          severity: 'error',
          summary: 'Error',
          detail: error.message || `Failed to trigger ${jobType} scheduling`,
          life: 3000,
        },
        { root: true },
      );

      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Load execution history with optional filtering and pagination
   */
  async loadExecutionHistory({ commit, state: currentState }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      // For now, we'll use the local execution history
      // In the future, you can implement API endpoint for history
      const history = currentState.executionHistory;

      commit('SET_EXECUTION_HISTORY', history);
      commit('SET_PAGINATION', {
        page: 1,
        limit: history.length,
        total: history.length,
        totalPages: 1,
      });

      return { history, total: history.length };
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to load execution history');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Clear all errors
   */
  clearErrors({ commit }) {
    commit('CLEAR_ERROR');
  },

  /**
   * Reset scheduler state
   */
  resetSchedulerState({ commit }) {
    commit('RESET_STATE');
  },
};

// Getters
const getters = {
  /**
   * Get all jobs with their current status
   */
  allJobs: (currentState) => currentState.jobs,

  /**
   * Get jobs by status
   */
  jobsByStatus: (currentState) => (status) => Object.entries(currentState.jobs)
    .filter(([, job]) => job.status === status)
    .reduce((acc, [key, job]) => {
      acc[key] = job;
      return acc;
    }, {}),

  /**
   * Get running jobs
   */
  runningJobs: (currentState) => Object.entries(currentState.jobs)
    .filter(([, job]) => job.status === 'running')
    .map(([key]) => key),

  /**
   * Get idle jobs
   */
  idleJobs: (currentState) => Object.entries(currentState.jobs)
    .filter(([, job]) => job.status === 'idle')
    .map(([key]) => key),

  /**
   * Get completed jobs
   */
  completedJobs: (currentState) => Object.entries(currentState.jobs)
    .filter(([, job]) => job.status === 'completed')
    .map(([key]) => key),

  /**
   * Get failed jobs
   */
  failedJobs: (currentState) => Object.entries(currentState.jobs)
    .filter(([, job]) => job.status === 'failed')
    .map(([key]) => key),

  /**
   * Get scheduler health status
   */
  schedulerHealth: (currentState) => {
    const failedJobs = Object.values(currentState.jobs).filter(
      (job) => job.status === 'failed',
    ).length;
    const runningJobs = Object.values(currentState.jobs).filter(
      (job) => job.status === 'running',
    ).length;

    if (failedJobs > 0) return 'error';
    if (runningJobs > 0) return 'warning';
    return 'healthy';
  },

  /**
   * Get recent execution history (last 10)
   */
  recentExecutions: (currentState) => currentState.executionHistory.slice(0, 10),

  /**
   * Get filtered execution history
   */
  filteredExecutions: (currentState) => {
    let filtered = currentState.executionHistory;

    if (currentState.filters.jobType) {
      filtered = filtered.filter((exec) => exec.type === currentState.filters.jobType);
    }

    if (currentState.filters.status) {
      filtered = filtered.filter((exec) => exec.status === currentState.filters.status);
    }

    return filtered;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
